/**
 * CRM Contact Detail Page — timeline-centric contact view with editable sidebar.
 */

import { useEffect, useState } from 'react'
import { DETAIL_TITLE_CLASS } from '@/components/layout/PageHeader'
import { cn } from '@/utils'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, MessageCircle, PanelRightOpen, PanelRightClose } from 'lucide-react'
import { useCrmStore } from '@/stores/crmStore'
import { useCRM } from '@/hooks/useCRM'
import { ContactTimeline } from '@/components/crm/ContactTimeline'
import { ContactSidebar } from '@/components/crm/ContactSidebar'
import { Spinner } from '@/components/ui'

export function ContactDetailPage() {
  const { contactId } = useParams<{ contactId: string }>()
  const navigate = useNavigate()
  const { fetchContactDetail, fetchTimeline } = useCRM()
  const contact = useCrmStore((s) => s.selectedContact)
  const isLoading = useCrmStore((s) => s.isLoadingDetail)
  const [showMobileSidebar, setShowMobileSidebar] = useState(false)

  useEffect(() => {
    if (contactId) {
      fetchContactDetail(contactId)
      fetchTimeline(contactId)
    }
    return () => {
      useCrmStore.getState().setSelectedContact(null)
      useCrmStore.getState().setTimelineEvents([], false, null)
    }
  }, [contactId, fetchContactDetail, fetchTimeline])

  if (isLoading && !contact) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner />
      </div>
    )
  }

  if (!contact) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <p>Contact not found</p>
        <button
          onClick={() => navigate('/crm/contacts')}
          className="mt-2 text-primary hover:text-primary/80 text-sm"
        >
          Back to contacts
        </button>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-border py-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/crm/contacts')}
            className="p-1.5 rounded-sm hover:bg-muted transition-colors"
          >
            <ArrowLeft className="w-4 h-4 text-muted-foreground" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h2 className={cn(DETAIL_TITLE_CLASS, 'truncate')}>
                {contact.displayName || contact.channelUserId}
              </h2>
              {/* Mobile sidebar toggle */}
              <button
                onClick={() => setShowMobileSidebar(!showMobileSidebar)}
                className="lg:hidden p-1.5 rounded-sm hover:bg-muted transition-colors ml-auto"
                title={showMobileSidebar ? 'Hide details' : 'Show details'}
              >
                {showMobileSidebar ? (
                  <PanelRightClose className="w-4 h-4 text-muted-foreground" />
                ) : (
                  <PanelRightOpen className="w-4 h-4 text-muted-foreground" />
                )}
              </button>
              <span className={`inline-flex items-center px-2 py-0.5 text-xs rounded-full font-medium ${
                contact.channel === 'whatsapp'
                  ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                  : 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
              }`}>
                {contact.channel === 'whatsapp' ? (
                  <><MessageCircle className="w-3 h-3 mr-1" />WhatsApp</>
                ) : (
                  contact.channel
                )}
              </span>
              {contact.isBlocked && (
                <span className="inline-flex items-center px-2 py-0.5 text-xs rounded-full font-medium bg-destructive/10 text-destructive">
                  Blocked
                </span>
              )}
              {contact.isDeleted && (
                <span className="inline-flex items-center px-2 py-0.5 text-xs rounded-full font-medium bg-muted text-muted-foreground">
                  Deleted
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground font-mono">{contact.channelUserId}</p>
          </div>
        </div>
      </div>

      {/* Body: Sidebar + Timeline */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar — always visible on desktop, toggleable on mobile */}
        <div className={`w-80 border-r border-border overflow-y-auto bg-background shrink-0 ${
          showMobileSidebar ? 'block absolute inset-y-0 right-0 z-20 shadow-lg lg:relative lg:shadow-none' : 'hidden lg:block'
        }`}>
          <ContactSidebar contact={contact} />
        </div>

        {/* Mobile sidebar overlay backdrop */}
        {showMobileSidebar && (
          <div
            className="lg:hidden fixed inset-0 bg-black/20 z-10"
            onClick={() => setShowMobileSidebar(false)}
          />
        )}

        {/* Timeline */}
        <div className="flex-1 overflow-y-auto">
          <ContactTimeline contactId={contact.id} />
        </div>
      </div>
    </div>
  )
}
